import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import {
  Button,
  BUTTON_THEME,
  Icon,
  IconButton,
  ICON_TYPES,
  Card,
  Divider,
} from '@st-operations/uikit/src/v2/index.js'
import WizardIcon from './wizard.svg'

import Header from '_components/header/index.jsx'
import MoreInfoDropdown from '../../../components/more-info-dropdown/index.jsx'
import { useEventFeedsContext } from '../context/index.jsx'
import { hasTaxonomyRequiredErrors } from '../query-panel/utils.js'
import { useDropdown, useModal } from '_hooks'
import DeliveryModal from '../delivery/index.jsx'
import CreateEventFeedModal from '../list-view/create-event-feed-modal/index.jsx'
import { logErrorAsync } from '../../../lib/utility.js'
import { getDaasClients } from '_services/daas-clients'
import { snapshot } from '_services/data-explorer'
import FeedNameEditor from './feed-name-editor'
import EventFeedsHeaderOnboardTooltips from '../header-onboard-tooltips/index.jsx'

import './styles.scss'

export const STATUSES = {
  DRAFT: 'draft',
  DELIVERED: 'delivered',
}

const INITIAL_STATE = {
  name: '',
  client: { _id: '', name: '' },
}

const EventFeedsHeader = ({
  onSave,
  onDelete,
  onRewatchOnboardingTooltips,
  loading,
  panelWidth,
  headerOnboardShown,
  onHeaderOnboardFinish,
  onRunWizard,
  onEnhancersClick,
}) => {
  const { data, id, permissions, dispatch, ACTIONS } = useEventFeedsContext()
  const moreInfo = useDropdown()
  const moreOptions = useDropdown()
  const wizardOptions = useDropdown()
  const deliveryModal = useModal()
  const createModal = useModal()

  const [newFeedValues, setNewFeedValues] = useState(INITIAL_STATE)
  const [allClients, setAllClients] = useState([])
  const [isSnapshotLoading, setIsSnapshotLoading] = useState(false)

  useEffect(() => {
    async function getEnums() {
      try {
        const allClients = await getDaasClients(['_id', 'name'])

        setAllClients(allClients.items)
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
    }
    getEnums()
  }, [])

  // Custom Taxonomy required fields (e.g. Topics Threshold, Feed Output Root) live
  // on the page, not in the modal. Highlight them instead of proceeding when invalid.
  const validateTaxonomy = () => {
    if (hasTaxonomyRequiredErrors(data)) {
      dispatch({ type: ACTIONS.SET_SHOW_TAXONOMY_ERRORS, payload: true })
      toast.error('There are some required fields that must be filled')
      return false
    }
    dispatch({ type: ACTIONS.SET_SHOW_TAXONOMY_ERRORS, payload: false })
    return true
  }

  const handleOpenDelivery = () => {
    if (!validateTaxonomy()) return
    deliveryModal.handleOpenModal()
  }

  const handleSave = () => {
    if (id !== 'discovery') {
      if (!validateTaxonomy()) return
      onSave()
    } else {
      createModal.handleOpenModal()
    }
  }

  const handleCreateFeed = async () => {
    try {
      createModal.handleCloseModal()
      onSave(true, newFeedValues)
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    }
  }

  const handleCreateSnapshot = async () => {
    setIsSnapshotLoading(true)
    try {
      await snapshot({ id })
      window._history.push('/event-feeds')
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    }
    setIsSnapshotLoading(false)
  }

  return (
    <>
      <Header className="audience-creator__header">
        <div className="audience-creator-header">
          <If condition={id !== 'discovery'}>
            <FeedNameEditor />
          </If>
          <If condition={id === 'discovery'}>
            <h2 className="title">Discovery</h2>
          </If>
          <Link to="/data-explorer">
            <Button className="audience-creator-header__button" theme={BUTTON_THEME.GHOST}>
              Exit
            </Button>
          </Link>
          <If condition={id !== 'discovery'}>
            {permissions?.configuration?.delivery.display && (
              <Button
                onClick={handleOpenDelivery}
                className="audience-creator-header__button"
                theme={BUTTON_THEME.SECONDARY}
              >
                {data?.deliveries?.[0]?.status === 'draft' ? 'Submit delivery' : 'Edit delivery'}
              </Button>
            )}
          </If>
          <Button
            isLoading={loading.isSaving}
            onClick={handleSave}
            className="audience-creator-header__button"
          >
            Save Changes
          </Button>

          <IconButton
            className="audience-creator-header__more-options"
            onClick={moreOptions.handleOpen}
            isActive={moreOptions.isOpen}
            aria-label="See more options"
            iconProps={{
              icon: 'more',
              type: ICON_TYPES.FILL,
              width: '32px',
              height: '32px',
            }}
            text="More options"
          />
          <Divider vertical className={'audience-creator-header__button-divider'} />
          <div
            className="audience-creator-header__wizard-container"
            onClick={wizardOptions.handleOpen}
            data-tooltip-direction="bottom"
            data-tooltip-text="Help"
          >
            <WizardIcon
              className="audience-creator-header__wizard"
              onClick={wizardOptions.handleOpen}
            />
          </div>
          <IconButton
            className="audience-creator-header__more-info"
            onClick={moreInfo.handleOpen}
            isActive={moreInfo.isOpen}
            aria-label="See additional help"
            iconProps={{
              icon: 'info',
              type: ICON_TYPES.OUTLINE,
              width: '32px',
              height: '32px',
            }}
            text="More info"
          />

          <If condition={moreOptions.isOpen}>
            <Card
              component="ul"
              className="audience-creator-header__dropdown"
              ref={moreOptions.dropdownRef}
            >
              <li>
                <Button
                  theme={BUTTON_THEME.GHOST}
                  className="audience-creator-header__dropdown-option"
                  onClick={onEnhancersClick}
                >
                  <Icon
                    type="outline"
                    icon="analytics"
                    color="#3D3D3D"
                    width="24px"
                    height="24px"
                  />
                  Enhancers
                </Button>
              </li>
              <li>
                <Button
                  theme={BUTTON_THEME.GHOST}
                  className="audience-creator-header__dropdown-option"
                  disabled={true}
                >
                  <Icon type="outline" icon="download" color="#3D3D3D" width="24px" height="24px" />
                  PDF Export
                </Button>
              </li>
              <If condition={id !== 'discovery'}>
                <li>
                  <Button
                    theme={BUTTON_THEME.GHOST}
                    className="audience-creator-header__dropdown-option"
                    onClick={handleCreateSnapshot}
                    isLoading={isSnapshotLoading}
                    disabled={true}
                  >
                    <Icon
                      type="outline"
                      icon="binoculars"
                      color="#3D3D3D"
                      width="24px"
                      height="24px"
                    />
                    Snapshot
                  </Button>
                </li>

                <Divider />
                <li>
                  <Button
                    theme={BUTTON_THEME.GHOST}
                    className="audience-creator-header__dropdown-option"
                    onClick={onDelete}
                    isLoading={loading.isDeleting}
                  >
                    <Icon
                      type="outline"
                      icon="trashcan"
                      color="#3D3D3D"
                      width="24px"
                      height="24px"
                    />
                    Delete
                  </Button>
                </li>
              </If>
            </Card>
          </If>
          <If condition={wizardOptions.isOpen}>
            <Card
              component="ul"
              className="audience-creator-header__dropdown"
              ref={wizardOptions.dropdownRef}
            >
              <li>
                <Button
                  theme={BUTTON_THEME.GHOST}
                  className="audience-creator-header__dropdown-option"
                  onClick={onRewatchOnboardingTooltips}
                >
                  Rewatch Onboarding
                </Button>
                <Button
                  theme={BUTTON_THEME.GHOST}
                  className="audience-creator-header__dropdown-option"
                  onClick={onRunWizard}
                >
                  Run Wizard
                </Button>
              </li>
            </Card>
          </If>
          <If condition={moreInfo.isOpen}>
            <MoreInfoDropdown ref={moreInfo.dropdownRef} onClose={moreInfo.handleClose} />
          </If>
          <DeliveryModal
            isOpen={deliveryModal.isOpen}
            onClose={deliveryModal.handleCloseModal}
            onSave={onSave}
          />
        </div>
      </Header>
      <CreateEventFeedModal
        modalOpen={createModal.isOpen}
        toggleModal={createModal.handleToggleModal}
        newFeedValues={newFeedValues}
        setNewFeedValues={setNewFeedValues}
        handleCreateFeed={handleCreateFeed}
        daasClients={allClients}
      />
      <If condition={headerOnboardShown}>
        <EventFeedsHeaderOnboardTooltips panelWidth={panelWidth} onFinish={onHeaderOnboardFinish} />
      </If>
    </>
  )
}

EventFeedsHeader.propTypes = {
  title: PropTypes.string,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  loading: PropTypes.shape({
    isSaving: PropTypes.bool,
    isDeleting: PropTypes.bool,
  }),
}

EventFeedsHeader.defaultProps = {
  title: '',
  onSave: () => {},
  onDelete: () => {},
  loading: {
    isSaving: false,
    isDeleting: false,
  },
}

export default EventFeedsHeader
